import React from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Overline,
  Reveal,
  PrimaryButton,
  SecondaryButton,
} from "@/components/primitives";

/* Product hero — shared family resemblance across product pages */
export function ProductHero({
  eyebrow,
  title,
  subhead,
  primaryLabel,
  secondaryLabel,
  secondaryTo = "#adjacent",
  image,
  testid,
}) {
  return (
    <section className="relative overflow-hidden border-b border-border" data-testid={testid}>
      {image && (
        <div className="absolute inset-0">
          <img
            src={image}
            alt=""
            className="h-full w-full object-cover opacity-[0.07] grayscale dark:opacity-[0.1]"
          />
        </div>
      )}
      <Container className="relative grid grid-cols-1 gap-10 py-24 md:py-32 lg:grid-cols-12">
        <div className="lg:col-span-10">
          <Reveal>
            <Overline accent>{eyebrow}</Overline>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-display text-5xl font-bold leading-[0.95] tracking-[-0.03em] text-foreground md:text-7xl">
              {title}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              {subhead}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-11 flex flex-col gap-4 sm:flex-row sm:items-center">
              <PrimaryButton to="/book-a-walkthrough" testid={`${testid}-primary`}>
                {primaryLabel}
              </PrimaryButton>
              {secondaryLabel && (
                <SecondaryButton to={secondaryTo} testid={`${testid}-secondary`} arrow>
                  {secondaryLabel}
                </SecondaryButton>
              )}
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

/* Editorial content section: index label + heading + body/children */
export function ContentSection({ n, eyebrow, title, children, className = "", testid }) {
  return (
    <section className={`border-b border-border py-20 md:py-28 ${className}`} data-testid={testid}>
      <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="flex items-baseline gap-4">
              {n && <span className="font-body text-sm font-medium tracking-widest text-accent">{n}</span>}
              {eyebrow && <Overline className="text-muted-foreground">{eyebrow}</Overline>}
            </div>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-4xl">
              {title}
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal delay={0.08}>{children}</Reveal>
        </div>
      </Container>
    </section>
  );
}

/* Adjacent tools / cross-sell strip — one restrained CTA */
export function AdjacentCrossSell({ title, body, to, label, testid }) {
  return (
    <section id="adjacent" className="border-b border-border bg-secondary/40 py-20 md:py-24" data-testid={testid}>
      <Container className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-8">
          <Overline accent>Add the next layer</Overline>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-4xl">
            {title}
          </h2>
          <p className="mt-5 max-w-2xl leading-relaxed text-muted-foreground">{body}</p>
        </div>
        <div className="lg:col-span-4 lg:text-right">
          <Link
            to={to}
            data-testid={`${testid}-btn`}
            className="inline-flex items-center gap-3 border border-foreground/40 px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-foreground transition-colors hover:border-accent hover:text-accent"
          >
            {label} <span>→</span>
          </Link>
        </div>
      </Container>
    </section>
  );
}

/* Product final CTA */
export function ProductFinalCTA({ title, body, label, testid }) {
  return (
    <section className="bg-primary py-24 text-primary-foreground md:py-28" data-testid={testid}>
      <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-8">
          <h2 className="font-display text-4xl font-semibold leading-[1.02] tracking-tight md:text-6xl">
            {title}
          </h2>
          {body && <p className="mt-6 max-w-xl leading-relaxed text-primary-foreground/70">{body}</p>}
        </div>
        <div className="lg:col-span-4 lg:text-right">
          <Link
            to="/book-a-walkthrough"
            data-testid={`${testid}-btn`}
            className="inline-flex items-center gap-3 bg-primary-foreground px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-primary transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {label} <span>→</span>
          </Link>
        </div>
      </Container>
    </section>
  );
}
